import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react'
import { CheckCircle2, LockKeyhole, Settings2, UserCircle2, Camera, Loader2, X } from 'lucide-react'
import { useAuthStore } from '../stores'
import { auth } from '../utils/backend'
import { isStrongPassword } from '../utils/security'
import { uploadToCloudinary } from '../utils/cloudinary'
import { initPayment } from '../utils/payments'
import getSubscription from '../utils/subscriptions'

function UpgradePanel({ user }: { user: any }) {
  const [hasActive, setHasActive] = useState(false)

  useEffect(() => {
    let mounted = true
    async function check() {
      try {
        const sub = await getSubscription()
        const active = !!(sub && (sub.status === 'active' || sub.active))
        if (mounted) setHasActive(active)
      } catch (e) {
        if (mounted) setHasActive(false)
      }
    }
    check()
    return () => { mounted = false }
  }, [user])

  if (hasActive) {
    return (
      <div className="rounded-2xl p-4 bg-green-900/10 border border-green-500/10 text-slate-300">
        You have an active subscription. Thank you for supporting Scouta.
      </div>
    )
  }

  return (
    <div className="rounded-2xl p-4 bg-gradient-to-r from-green-500/5 to-green-200/5 border border-green-500/10 flex items-center justify-between">
      <div>
        <p className="text-sm font-bold text-green-300">Go Pro</p>
        <p className="text-xs text-slate-300">Upgrade your account to unlock premium features for your role.</p>
      </div>
      <div>
        <button
          onClick={async () => {
            try {
              const product = user.role === 'player' ? 'player_pro' : user.role === 'scout' ? 'scout_pro' : 'club_pro'
              const { authorization_url } = await initPayment(product)
              window.location.href = authorization_url
            } catch (err: any) {
              alert(err.message || 'Failed to start payment')
            }
          }}
          className="px-4 py-2 rounded-xl bg-green-500 text-black font-bold"
        >
          Upgrade Account
        </button>
      </div>
    </div>
  )
}

function SettingsPanel() {
  const { user, setUser, logout } = useAuthStore()
  const [fullName, setFullName] = useState(user?.name || '')
  const [org, setOrg] = useState(user?.org || '')
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMessage, setProfileMessage] = useState('')
  const [profileError, setProfileError] = useState('')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState('')
  const [passwordError, setPasswordError] = useState('')

  useEffect(() => {
    setFullName(user?.name || '')
    setOrg(user?.org || '')
    setAvatarUrl(user?.avatarUrl || '')
  }, [user?.name, user?.org, user?.avatarUrl])

  const handleAvatarUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setAvatarError('')
    setProfileMessage('')

    try {
      setUploadingAvatar(true)
      const upload = await uploadToCloudinary(file, 'scoutang/avatars')
      setAvatarUrl(upload.secure_url)
      await auth.updateAccountProfile({ avatarUrl: upload.secure_url })

      if (user) {
        setUser({
          ...user,
          avatarUrl: upload.secure_url
        })
      }

      setProfileMessage('Profile photo updated successfully.')
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Failed to upload avatar')
    } finally {
      setUploadingAvatar(false)
      e.target.value = ''
    }
  }

  const removeAvatar = async () => {
    setAvatarError('')
    setProfileMessage('')

    try {
      setUploadingAvatar(true)
      setAvatarUrl('')
      await auth.updateAccountProfile({ avatarUrl: '' })

      if (user) {
        setUser({
          ...user,
          avatarUrl: ''
        })
      }

      setProfileMessage('Profile photo removed.')
    } catch (err) {
      setAvatarError(err instanceof Error ? err.message : 'Failed to remove avatar')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleProfileSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProfileError('')
    setProfileMessage('')

    if (!fullName.trim()) {
      setProfileError('Display name is required')
      return
    }

    setSavingProfile(true)
    try {
      await auth.updateAccountProfile({
        fullName: fullName.trim(),
        org: org.trim()
      })

      if (user) {
        setUser({
          ...user,
          name: fullName.trim(),
          org: org.trim(),
          initials: fullName.trim().charAt(0).toUpperCase()
        })
      }

      setProfileMessage('Profile updated successfully.')
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setSavingProfile(false)
    }
  }

  const handlePasswordSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordMessage('')

    if (!newPassword) {
      setPasswordError('New password is required')
      return
    }

    if (!confirmPassword) {
      setPasswordError('Please confirm your new password')
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    if (!isStrongPassword(newPassword)) {
      setPasswordError('Password must be 8+ chars with uppercase, lowercase, and numbers')
      return
    }

    setSavingPassword(true)
    try {
      await auth.updateUserPassword(newPassword)
      setPasswordMessage('Password updated successfully.')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password')
    } finally {
      setSavingPassword(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div className="rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top_right,_rgba(124,255,1,0.1),_transparent_30%),linear-gradient(160deg,_rgba(7,20,26,0.98),_rgba(4,12,16,0.98))] p-6 sm:p-8 shadow-2xl shadow-black/20">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[10px] font-black uppercase tracking-[0.35em] text-[#7cff01]">
              <Settings2 size={12} />
              Account settings
            </div>
            <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">
              Manage your profile and security from one place.
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-400">
              Every user can update their display name, organization name, and password here.
              The changes sync back to Supabase so they persist after refresh.
            </p>
          </div>

          <div className="grid gap-3 rounded-3xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300 sm:grid-cols-3 lg:min-w-[360px]">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Email</p>
              <p className="mt-2 break-all text-white">{user?.email}</p>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Role</p>
              <p className="mt-2 text-white">{user?.role === 'scout' ? 'Scout' : user?.role === 'sub_admin' ? 'Sub-Admin' : user?.role?.toUpperCase()}</p>
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">ID</p>
              <p className="mt-2 break-all text-white">{user?.id}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* Upgrade CTA for all account types */}
        <div className="col-span-2">
          {user && (
            <UpgradePanel user={user} />
          )}
        </div>
        <section className="rounded-[28px] border border-white/10 bg-[#07141a] p-6 shadow-xl shadow-black/20">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7cff01]/10 text-[#7cff01]">
              <UserCircle2 size={20} />
            </div>
            <div>
              <h3 className="text-xl font-black text-white">Profile details</h3>
              <p className="text-sm text-slate-400">Update the name and organization shown across the app.</p>
            </div>
          </div>

          <form onSubmit={handleProfileSave} className="mt-6 space-y-4">
            <div className="rounded-[24px] border border-white/10 bg-black/20 p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative h-20 w-20 overflow-hidden rounded-3xl border border-white/10 bg-black/30">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar preview" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-500">
                        <UserCircle2 size={28} />
                      </div>
                    )}

                    {uploadingAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                        <Loader2 className="h-5 w-5 animate-spin text-[#7cff01]" />
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-black uppercase tracking-widest text-white">
                      Profile photo
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      Upload a square image for the best result. JPEG, PNG, WebP, and GIF are supported.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-[#7cff01] px-4 py-3 text-sm font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a]">
                    <Camera size={16} />
                    Upload
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      disabled={uploadingAvatar}
                    />
                  </label>

                  {avatarUrl && (
                    <button
                      type="button"
                      onClick={removeAvatar}
                      disabled={uploadingAvatar}
                      className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-black uppercase tracking-widest text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <X size={16} />
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {avatarError && (
                <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {avatarError}
                </div>
              )}
            </div>

            {profileError && (
              <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {profileError}
              </div>
            )}

            {profileMessage && (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{profileMessage}</span>
                </div>
              </div>
            )}

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                Display name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                Organization
              </label>
              <input
                type="text"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder="Club, agency, or company name"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              />
              <p className="mt-2 text-xs text-slate-500">
                This is saved in your Supabase auth profile and shown wherever the app uses your organization.
              </p>
            </div>

            <button
              type="submit"
              disabled={savingProfile}
              className="w-full rounded-2xl bg-[#7cff01] px-4 py-3 font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              {savingProfile ? 'Saving...' : 'Save profile'}
            </button>
          </form>
        </section>

        <section className="rounded-[28px] border border-white/10 bg-[#07141a] p-6 shadow-xl shadow-black/20">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#7cff01]/10 text-[#7cff01]">
              <LockKeyhole size={20} />
            </div>
            <div>
              <h3 className="text-xl font-black text-white">Security</h3>
              <p className="text-sm text-slate-400">Set a new password without leaving the dashboard.</p>
            </div>
          </div>

          <form onSubmit={handlePasswordSave} className="mt-6 space-y-4">
            {passwordError && (
              <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {passwordError}
              </div>
            )}

            {passwordMessage && (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{passwordMessage}</span>
                </div>
              </div>
            )}

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                New password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 pr-12 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 flex items-center px-4 text-slate-400 transition hover:text-white"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-400">
                Confirm password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter new password"
                autoComplete="new-password"
                className="w-full rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-white placeholder:text-slate-500 focus:border-[#7cff01]/40 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/10"
              />
            </div>

            <p className="text-xs text-slate-500">
              Passwords must be at least 8 characters and include uppercase, lowercase, and numbers.
            </p>

            <button
              type="submit"
              disabled={savingPassword}
              className="w-full rounded-2xl bg-[#7cff01] px-4 py-3 font-black uppercase tracking-widest text-black transition hover:bg-[#8eff1a] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              {savingPassword ? 'Updating...' : 'Update password'}
            </button>
          </form>
        </section>
      </div>

      <div className="rounded-[28px] border border-white/10 bg-[#07141a] p-6 shadow-xl shadow-black/20">
        <h3 className="text-xl font-black text-white">Session</h3>
        <p className="mt-2 text-sm text-slate-400">
          If you are on a shared device, sign out when you are finished.
        </p>

        <button
          type="button"
          onClick={logout}
          className="mt-5 rounded-2xl border border-red-900/30 bg-red-900/10 px-4 py-3 text-sm font-black uppercase tracking-widest text-red-300 transition hover:bg-red-900/20"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

export default SettingsPanel
